import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, TextField } from '@mui/material';
import {
    Continued,
    Discontinued,
    RhColumns,
    RhNumberField,
    RhSingleAutocomplete,
} from '../../../../../dum';
import { FIELD_LABELS, shortLabel } from '../../../../../../helpers';

// One row of an account's product catalog. Owned here (the presentational
// piece) and reused by the account form, which now edits the catalog inline.
export interface IAccountProductRow {
    id?: number | null;
    product?: {
        id: number;
        compound_description: string;
        current_group_weight?: number | null;
        discontinued?: boolean | null;
        product_category?: { id: number; name: string } | null;
    } | null;
    kilo_price: number;
    group_price: number;
    group_weight?: number | null;
}

export const ACCOUNT_PRODUCTS = 'account_products';

type ProductOption = {
    id: number;
    compound_description: string;
    current_group_weight?: number | null;
    product_category?: { id: number; name: string } | null;
};

const ARRAY_NAME = ACCOUNT_PRODUCTS;

const PRODUCT: keyof IAccountProductRow = 'product';
const KILO_PRICE: keyof IAccountProductRow = 'kilo_price';
const GROUP_PRICE: keyof IAccountProductRow = 'group_price';
const GROUP_WEIGHT: keyof IAccountProductRow = 'group_weight';

const defaultValue: IAccountProductRow = {
    product: null,
    kilo_price: 0,
    group_price: 0,
    group_weight: 0,
};

// Read-only status indicator for a catalog row, matching the inventory product
// table: a green check for active products, a red cross for discontinued ones.
// Empty rows (no product picked yet) show nothing.
function CatalogStatusField({ index }: { index: number }) {
    const product = useWatch({
        name: `${ARRAY_NAME}.${index}.${PRODUCT}`,
    }) as IAccountProductRow['product'];

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {!product ? null : product.discontinued ? (
                <Discontinued color={'error'} />
            ) : (
                <Continued color={'success'} />
            )}
        </Box>
    );
}

// Read-only category of the row's product — derived from the picked product, not
// a persisted catalog field. Disabled input shown for context (the catalog is
// sorted by category, so this makes the grouping legible).
function CatalogCategoryField({ index }: { index: number }) {
    const product = useWatch({
        name: `${ARRAY_NAME}.${index}.${PRODUCT}`,
    }) as IAccountProductRow['product'];

    return (
        <TextField
            value={product?.product_category?.name ?? ''}
            label={shortLabel(FIELD_LABELS.product_category_id)}
            disabled
            size={'small'}
            margin={'dense'}
            fullWidth
        />
    );
}

export function AccountProductsColumns({
    products,
}: {
    products: ProductOption[];
}) {
    const formContext = useFormContext();

    return (
        <RhColumns
            title={'Productos del catálogo'}
            name={ARRAY_NAME}
            defaultValue={defaultValue}
            items={[
                {
                    cellGrow: 3,
                    getField: (index) => {
                        return (
                            <RhSingleAutocomplete
                                itemText={'compound_description'}
                                itemValue={'id'}
                                returnObject
                                options={products}
                                name={`${ARRAY_NAME}.${index}.${PRODUCT}`}
                                label={shortLabel(FIELD_LABELS.product_id)}
                                disableGutters
                                onChange={(product: ProductOption | null) => {
                                    // Group weight is a product attribute, shown
                                    // read-only; the backend re-derives it on save.
                                    formContext.setValue(
                                        `${ARRAY_NAME}.${index}.${GROUP_WEIGHT}`,
                                        product?.current_group_weight || 0,
                                    );
                                }}
                            />
                        );
                    },
                },
                {
                    cellGrow: 2,
                    getField: (index) => {
                        return <CatalogCategoryField index={index} />;
                    },
                },
                {
                    getField: (index) => {
                        return (
                            <RhNumberField
                                name={`${ARRAY_NAME}.${index}.${KILO_PRICE}`}
                                label={shortLabel(FIELD_LABELS.kilo_price)}
                                disableGutters
                            />
                        );
                    },
                },
                {
                    getField: (index) => {
                        return (
                            <RhNumberField
                                name={`${ARRAY_NAME}.${index}.${GROUP_PRICE}`}
                                label={shortLabel(FIELD_LABELS.group_price)}
                                disableGutters
                            />
                        );
                    },
                },
                {
                    getField: (index) => {
                        return (
                            <RhNumberField
                                disabled
                                name={`${ARRAY_NAME}.${index}.${GROUP_WEIGHT}`}
                                label={shortLabel(FIELD_LABELS.group_weight)}
                                disableGutters
                            />
                        );
                    },
                },
                {
                    getField: (index) => {
                        return <CatalogStatusField index={index} />;
                    },
                },
            ]}
        />
    );
}
